import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Lightbulb, Sparkles, Users, TrendingUp } from 'lucide-react';

const Landing = () => {
  const { user } = useAuth();
  const navigate = useNavigate();

  if (user) {
    navigate('/dashboard');
    return null;
  }

  return (
    <div className="min-h-screen bg-[#FAFAF9]">
      {/* Header */}
      <header className="sticky top-0 z-50 w-full border-b border-zinc-200 bg-white/80 backdrop-blur supports-[backdrop-filter]:bg-white/60">
        <div className="container mx-auto px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img src="/gba-logo.png" alt="GBA Logo" className="h-10 w-auto" />
            <span className="text-xl font-bold text-[#4A5A7C]">GBA Idea App</span>
          </div>
          <div className="flex items-center gap-3">
            <Link
              to="/login"
              data-testid="header-login-button"
              className="text-zinc-600 hover:text-zinc-900 rounded-lg px-4 py-2 font-medium transition-colors"
            >
              Sign in
            </Link>
            <Link
              to="/signup"
              data-testid="header-signup-button"
              className="bg-[#5B6B8F] text-white hover:bg-[#4A5A7C] shadow-sm rounded-lg px-4 py-2 font-medium transition-all active:scale-95"
            >
              Get started
            </Link>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="container mx-auto px-6 py-20 md:py-32">
        <div className="max-w-4xl mx-auto text-center">
          <div className="inline-flex items-center gap-2 bg-[#E8F5E9] border border-[#8BC34A] rounded-full px-4 py-2 mb-6">
            <Sparkles className="h-4 w-4 text-[#689F38]" />
            <span className="text-sm font-medium text-[#1B5E20]">Gaurav Bhagat Academy - Idea Management</span>
          </div>
          <h1 className="text-5xl md:text-6xl font-bold text-zinc-900 mb-6 tracking-tight">
            Your ideas deserve
            <br />
            <span className="text-[#5B6B8F]">better organization</span>
          </h1>
          <p className="text-xl text-zinc-600 mb-4 max-w-2xl mx-auto leading-relaxed">
            Capture, organize, and bring your ideas to life with powerful team collaboration, progress tracking, and accountability tools.
          </p>
          <p className="text-lg text-[#689F38] font-semibold mb-10">
            ENHANCE • ELEVATE • EXCEL
          </p>
          <div className="flex items-center justify-center gap-4">
            <Link
              to="/signup"
              data-testid="hero-get-started-button"
              className="bg-[#5B6B8F] text-white hover:bg-[#4A5A7C] shadow-md rounded-lg px-8 py-3 font-semibold text-lg transition-all active:scale-95 hover:shadow-lg"
            >
              Get started free
            </Link>
            <Link
              to="/login"
              data-testid="hero-login-button"
              className="bg-white text-zinc-900 border border-zinc-200 hover:bg-zinc-50 hover:border-zinc-300 shadow-sm rounded-lg px-8 py-3 font-semibold text-lg transition-all active:scale-95"
            >
              Sign in
            </Link>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="container mx-auto px-6 py-20">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-zinc-900 mb-4">Everything you need to manage ideas</h2>
            <p className="text-lg text-zinc-600">A complete toolkit for idea management and team collaboration</p>
          </div>
          
          <div className="grid md:grid-cols-3 gap-8">
            <div className="bg-white border border-zinc-200 rounded-xl p-8 shadow-sm hover:shadow-md transition-shadow">
              <div className="h-12 w-12 rounded-lg bg-[#E8F5E9] flex items-center justify-center mb-4">
                <Lightbulb className="h-6 w-6 text-[#689F38]" />
              </div>
              <h3 className="text-xl font-semibold text-zinc-900 mb-2">Capture & Organize</h3>
              <p className="text-zinc-600 leading-relaxed">
                Log ideas instantly with categories, custom tags, and rich descriptions. Search and filter to find anything in seconds.
              </p>
            </div>

            <div className="bg-white border border-zinc-200 rounded-xl p-8 shadow-sm hover:shadow-md transition-shadow">
              <div className="h-12 w-12 rounded-lg bg-[#E8F5E9] flex items-center justify-center mb-4">
                <TrendingUp className="h-6 w-6 text-[#689F38]" />
              </div>
              <h3 className="text-xl font-semibold text-zinc-900 mb-2">Track Progress</h3>
              <p className="text-zinc-600 leading-relaxed">
                Monitor idea development with status tracking and progress percentages. See what's moving forward at a glance.
              </p>
            </div>

            <div className="bg-white border border-zinc-200 rounded-xl p-8 shadow-sm hover:shadow-md transition-shadow">
              <div className="h-12 w-12 rounded-lg bg-[#E8F5E9] flex items-center justify-center mb-4">
                <Users className="h-6 w-6 text-[#689F38]" />
              </div>
              <h3 className="text-xl font-semibold text-zinc-900 mb-2">Team Collaboration</h3>
              <p className="text-zinc-600 leading-relaxed">
                Assign ideas to team members and track accountability. Keep everyone aligned on what matters most.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="container mx-auto px-6 py-20">
        <div className="max-w-4xl mx-auto bg-gradient-to-br from-[#E8F5E9] to-[#C8E6C9] border border-[#8BC34A] rounded-2xl p-12 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-zinc-900 mb-4">
            Start capturing ideas today
          </h2>
          <p className="text-lg text-zinc-600 mb-8 max-w-2xl mx-auto">
            Join teams who are already transforming how they manage and develop ideas with GBA Idea App.
          </p>
          <Link
            to="/signup"
            data-testid="cta-get-started-button"
            className="inline-block bg-[#5B6B8F] text-white hover:bg-[#4A5A7C] shadow-md rounded-lg px-8 py-3 font-semibold text-lg transition-all active:scale-95 hover:shadow-lg"
          >
            Get started free
          </Link>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-zinc-200 bg-white py-8">
        <div className="container mx-auto px-6">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              <img src="/gba-logo.png" alt="GBA Logo" className="h-8 w-auto" />
              <div>
                <span className="text-lg font-bold text-[#4A5A7C]">GBA Idea App</span>
                <p className="text-xs text-[#689F38] font-semibold">ENHANCE • ELEVATE • EXCEL</p>
              </div>
            </div>
            <p className="text-sm text-zinc-500">© 2026 Gaurav Bhagat Academy. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Landing;